import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CommentsTab from '../CommentsTab/CommentsTab';
import styles from './StoryboardRightSidebar.module.scss';
import cx from 'classnames';
import TooltipButton from '../TooltipButton/TooltipButton';
import { OrientationsDropdown } from 'engage/features/Storyboard/components/OrientationsDropdown/OrientationsDropdown';
import Icon from '../Icon/Icon';
import UsersListPreview from '../UsersListPreview/UsersListPreview';
import { useStoryboardContext } from 'engage/context/StoryboardContext';
import { useUserContext } from 'engage/context/UserContext';
import { CustomButton } from '../Button/Button';
import { AccordionItem } from '../Accordion/AccordionItem';
import useWindowSize from 'engage/hooks/useWindowSize';

const MIN_SCREEN_HEIGHT = 800;
const BOTTOM_OFFSET = 30;

const commentInit = {
  authorName: '',
  content: '',
  parent: null,
  type: 3,
  username: '',
};

const StoryboardRightSidebar = ({
  isEditingOptionsEnabled,
  comments,
  loadComments,
  isCommentsLoading,
  storySchedule,
  videoOrientation,
  onSchedule,
  usersArray,
  onViewTeam,
  onInvite,
  openStoryboardAssistant,
  onUseAvatarOpen
}) => {
  const { t } = useTranslation();
  const [newComment, setNewComment] = useState(commentInit);
  const [commentsMaxHeight, setCommentsMaxHeight] = useState(0);

  const commentsContainerRef = useRef(null);

  const {
    isWebInvitationEnabled,
    isContentScheduleEnabled,
    isStoryboardAssistantEnabled,
    isAvatarEnabled
  } = useUserContext();

  const { height } = useWindowSize();

  const { videoId, scenes, changeScenesOrientation } = useStoryboardContext();

  const parent = useRef();

  const storyScheduled = isWebInvitationEnabled && Object.keys(storySchedule).length > 0;

  const accordionItems = useMemo(() => {
    const options = [];
    if (isEditingOptionsEnabled) {
      options.push({
        title: <h2 className={styles.header}>{t('general.settings')}</h2>,
        content: <div className={styles.sectionWrapper}>
          {isWebInvitationEnabled && (
            <div className={styles.teamContainer} ref={parent} style={{ width: 'fit-content' }}>
              <span className={styles.teamText}>{t('storyboard.editor.team')}</span>
              <div className={styles.userlistContainer} onClick={onViewTeam}>
                {usersArray.length ? <UsersListPreview maxShowAmount={3} parentRef={parent} users={usersArray} className={styles.usersList} size={'medium'} /> : null}
              </div>
              <button className={cx(styles.inviteBtn, { [styles.absolutePosition]: usersArray.length > 0 })} aria-label={t('storyboard.editor.manage_team.invite')} onClick={onInvite}>
                <Icon src="plus" className={styles.inviteIcon} />
              </button>
            </div>
          )}
          <div className={styles.section}>
            <OrientationsDropdown videoOrientation={videoOrientation} scenes={scenes} onChange={changeScenesOrientation} />
            {isContentScheduleEnabled ? <div className={styles.section}>
              <div className={styles.separator} />
              <TooltipButton
                onClick={onSchedule}
                variant="link"
                className={styles.scheduleBtn}
                tooltipText={t(storyScheduled ? 'storyboard.editor.schedule.change' : 'storyboard.editor.schedule.create')}
                classNameTooltip={styles.tooltip}
              >
                <Icon src="schedule" className={styles.scheduleIcon} />
                {storyScheduled ? <Icon src="checked" className={styles.checkMarkIcon} /> : null}
              </TooltipButton>
            </div> : null}
          </div>
        </div>
      });
      if (isStoryboardAssistantEnabled || isAvatarEnabled) {
        options.push({
          title: <h2 className={styles.header}>{t('my_video.tools.title')}</h2>,
          content: <div className={styles.toolsContainer}>
            {isStoryboardAssistantEnabled && (
              <CustomButton variant="link" className={styles.actionBtn} onClick={openStoryboardAssistant}>
                <Icon src="scriptGenerator" className={cx(styles.actionIcon, styles.scriptGeneratorIcon)} />
                <span>{t('storyboard.editor.assistant')}</span>
              </CustomButton>
            )}
            {isAvatarEnabled ? (
              <TooltipButton
                onClick={onUseAvatarOpen}
                variant="link"
                disabled={scenes.every(scene => !scene.script)}
                tooltipText={scenes.every(scene => !scene.script) ? t('storyboard.generate_avatar.disabled_tooltip') : ''}
                className={styles.actionBtn}
                tooltipClassName={styles.tooltip}
              >
                <Icon src="avatar" className={cx(styles.actionIcon, styles.avatarIcon)} />
                <span>{t('storyboard.editor.use_avatar')}</span>
              </TooltipButton>
            ) : null}
          </div>,
        });
      }
    }
    return options;
  }, [
    isEditingOptionsEnabled,
    isStoryboardAssistantEnabled,
    isAvatarEnabled,
    isWebInvitationEnabled,
    onInvite,
    videoOrientation,
    changeScenesOrientation,
    isContentScheduleEnabled,
    onSchedule,
    storyScheduled,
    usersArray.length,
    onViewTeam,
    scenes,
    openStoryboardAssistant,
    onUseAvatarOpen,
    t
  ]);

  const calculateCommentsMaxHeight = useCallback(() => {
    if (commentsContainerRef.current) {
      const { top } = commentsContainerRef.current.getBoundingClientRect();
      setCommentsMaxHeight(height - top - BOTTOM_OFFSET);
    }
  }, [height]);

  useEffect(() => {
    if (commentsContainerRef.current) {
      calculateCommentsMaxHeight();
    }
  }, [height]);

  return (
    <div className={styles.sidebarWrapper}>
      <div className={styles.sidebarContainer}>
        {accordionItems.map((item, index) => <AccordionItem className={styles.sectionWrapper} key={`accordion-item-${index}`} item={item} defaultState={window.innerHeight > MIN_SCREEN_HEIGHT || usersArray.length == 0} onTransitionEnd={calculateCommentsMaxHeight} />)}
        {isWebInvitationEnabled && !isCommentsLoading ? <div className={cx(styles.sectionWrapper)}>
          <h2 className={styles.header}>{t('sidebar.profile.chat')}</h2>
          <div className={styles.commentsContainer} ref={commentsContainerRef} style={{ maxHeight: commentsMaxHeight }}>
            {usersArray?.length > 0 ?
              <CommentsTab
                noCommentsMessage={!isCommentsLoading && comments?.length === 0 && t('storyboard.editor.no_comments_message')}
                updateComments={loadComments}
                isCommentsLoading={isCommentsLoading}
                storyId={videoId}
                comments={comments}
                commentInit={commentInit}
                newComment={newComment}
                setNewComment={setNewComment}
                highlightFirst={false}
              /> : <div className={styles.activateChatContainer}>
                <p className={styles.message}>{t('storyboard.activate_chat')}</p >
                <button className={cx(styles.inviteBtn)} aria-label={t('storyboard.editor.manage_team.invite')} onClick={onInvite}>
                  <Icon src="plus" className={styles.inviteIcon} />
                </button>
              </div>}
          </div>
        </div> : null}
      </div>
    </div>
  );
};

export default StoryboardRightSidebar;
